import React, { useEffect, useState } from "react";
import ResponsiveDrawer from "../../component/sidebar/sidebar.component";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontWeight } from "../../component/theme/fontWeight";
import { fontSize } from "../../component/theme/fontSize";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Modal,
  InputAdornment,
} from "@mui/material";
import { ThemeColor } from "../../component/theme/color";
import CustomDataTable from "../../component/datatable/DataTable";
import CustomButton from "../../component/button/button";
import { useDispatch, useSelector } from "react-redux";
import { GetCountryAction } from "../../Redux/action/SettingAction";
import { showSnackbar } from "../../Redux/action/SnackbarAction";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../../component/Modal/ConfirmationModal/ConfirmationModal";
import { DeleteCountryAvailableService } from "../../Redux/service/SettingService";
import DeleteIcon from "@mui/icons-material/Delete";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Crossicon from "../../Icon/Crossicon";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomPagination } from "../../component/datatable/CustomPagination";
import GlobalSearch from "../../component/sidebar/globalSearch";

/** Only these roles may add, edit or delete a country. */
const canManageCountries = () =>
  ["admin", "user"].includes(
    window.sessionStorage.getItem("user_role_type")
  );

const CountryListPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [Countrylist, setCountrylist] = useState([]);
  const [table_load, setTable_load] = useState(false);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);

  const [deleteconfirmationopen, setDeleteConfirmationOpen] = useState(false);
  const [deleteId, setDeleteId] = useState();

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [stateSearchQuery, setStateSearchQuery] = useState("");
  const [isViewStatesModalOpen, setIsViewStatesModalOpen] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const manageAllowed = canManageCountries();

  const handleViewStates = (event, countryName, statesList) => {
    event.stopPropagation();
    setSelectedCountry({ name: countryName, states: statesList || [] });
    setStateSearchQuery("");
    setIsViewStatesModalOpen(true);
  };

  const handlePageChange = (page) => {
    setTable_load(true);
    dispatch(GetCountryAction({ page, limit }));
  };

  const handleLimitChange = (newLimit) => {
    setTable_load(true);
    setLimit(newLimit);
    dispatch(GetCountryAction({ page: 1, limit: newLimit }));
  };

  useEffect(() => {
    setTimeout(() => setLoading(false), 2000);
  }, []);

  const CountrylistState = useSelector(
    (state) => state.setting?.countryCount?.data
  );

  useEffect(() => {
    if (CountrylistState) setPagination(CountrylistState?.pagination);
  }, [CountrylistState]);

  useEffect(() => {
    dispatch({ type: "GetCountryList", payload: null });
    setTable_load(true);
    dispatch(GetCountryAction({ page: 1, limit: 50 }));
  }, [dispatch]);

  useEffect(() => {
    // The country name is the row key: the list endpoint strips _id, and delete
    // works by name. Using the array index made rows shuffle between pages.
    setCountrylist(
      CountrylistState?.data?.map((data) => ({
        id: data.country,
        ...data,
      }))
    );
    setTable_load(false);
  }, [CountrylistState]);

  const columns = [
    {
      field: "country",
      headerName: "Country",
      width: 220,
      renderCell: (params) => (
        <Typography
          sx={{ fontSize: 14, fontWeight: 600, color: "#101828" }}
        >
          {params.row.country}
        </Typography>
      ),
    },
    {
      field: "stateCount",
      headerName: "States",
      width: 110,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.states?.length || 0}
          sx={{
            height: 22,
            minWidth: 40,
            fontWeight: 700,
            fontSize: 12,
            bgcolor: "rgba(19,85,155,0.10)",
            color: "rgba(19,85,155,1)",
          }}
        />
      ),
    },
    {
      field: "states",
      headerName: "State Names",
      flex: 1,
      minWidth: 320,
      renderCell: (params) =>
        params.row.states?.length ? (
          <Tooltip title="Click to view the full state list">
            <Box
              onClick={(e) =>
                handleViewStates(e, params.row.country, params.row.states)
              }
              sx={{
                color: "rgba(19, 85, 155, 1)",
                fontWeight: 500,
                cursor: "pointer",
                textDecoration: "underline",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              {params.row.states.join(", ")}
            </Box>
          </Tooltip>
        ) : (
          <Typography sx={{ fontSize: 13, color: "#98A2B3" }}>
            No states added
          </Typography>
        ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View states">
            <IconButton
              size="small"
              onClick={(event) =>
                handleViewStates(event, params.row.country, params.row.states)
              }
            >
              <RemoveRedEyeIcon
                fontSize="small"
                sx={{ fill: "rgba(19, 85, 155, 1)" }}
              />
            </IconButton>
          </Tooltip>

          {manageAllowed && (
            <Tooltip title="Edit country">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(
                    `/CAE/AddCountry/${encodeURIComponent(params.row.country)}`
                  );
                }}
              >
                <ModeEditIcon
                  fontSize="small"
                  sx={{ fill: ThemeColor.LIGHTPURPLE }}
                />
              </IconButton>
            </Tooltip>
          )}

          {manageAllowed && (
            <Tooltip title="Delete country">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setDeleteId(params.row.country);
                  setDeleteConfirmationOpen(true);
                }}
              >
                <DeleteIcon fontSize="small" sx={{ fill: ThemeColor.RED }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <div>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            fontSize: "24px",
            color: "#007bff",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100px",
              height: "100px",
              borderRadius: "50%",
              overflow: "hidden",
              backgroundColor: "transparent",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                justifyContent: "center",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src="/assest/Group.png"
                alt="Loading..."
                style={{ width: "75%", height: "75%", objectFit: "contain" }}
              />
            </div>

            <CircularProgress
              size={100}
              sx={{
                color: ThemeColor.NAVYBLUE,
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 1,
              }}
            />
          </div>
        </div>
      ) : (
        <>
          <Grid container spacing={2} alignItems="center">
            <Grid item lg={8} md={7} xs={12}>
              <div className="d-flex align-items-center">
                <IconButton onClick={() => navigate(-1)}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography
                  fontFamily={FontFamily.Poppins}
                  fontWeight={fontWeight.W600}
                  fontSize={fontSize.h2.small}
                >
                  Country List
                </Typography>
                {pagination?.totalCount ? (
                  <Typography
                    className="px-2 pt-1"
                    fontFamily={FontFamily.Poppins}
                    fontSize={fontSize.h4.medium}
                    fontWeight={fontWeight.W300}
                    color={ThemeColor.GREY}
                  >
                    {pagination.totalCount}{" "}
                    {pagination.totalCount === 1 ? "country" : "countries"}
                  </Typography>
                ) : null}
              </div>
            </Grid>
            <Grid item lg={4} md={5} xs={12}>
              <Box
                className="d-flex align-items-center w-100"
                sx={{
                  justifyContent: {
                    xs: "flex-start",
                    sm: "flex-start",
                    md: "flex-end",
                    lg: "flex-end",
                  },
                }}
              >
                {manageAllowed && (
                  <CustomButton
                    variant="contained"
                    type="button"
                    backgroundColor={ThemeColor.PURPLE}
                    color={ThemeColor.WHITE}
                    onClick={() => navigate("/CAE/AddCountry")}
                  >
                    <AddIcon fontSize="small" className="me-1" />
                    Add Country
                  </CustomButton>
                )}
              </Box>
            </Grid>
          </Grid>

          <div className="table-container mt-4" style={{ overflowX: "hidden" }}>
            <CustomDataTable
              row={Countrylist ? Countrylist : []}
              columns={columns}
              loading={table_load}
              minTableWidth="100%"
            />
          </div>

          <CustomPagination
            pagination={pagination}
            onPageChange={handlePageChange}
            limit={limit}
            onLimitChange={handleLimitChange}
          />

          <ConfirmationModal
            msg="Delete"
            btnName="Delete"
            handleConfirmationClose={() => setDeleteConfirmationOpen(false)}
            deletefunction={async () => {
              try {
                const res = await DeleteCountryAvailableService(deleteId);

                if (res.status === 200) {
                  dispatch(GetCountryAction({ page: 1, limit }));
                  return res;
                }
                return Promise.reject("Unexpected response status");
              } catch (e) {
                dispatch(
                  showSnackbar(
                    e.response?.data?.message || "Error occurred",
                    "error"
                  )
                );
                return e;
              }
            }}
            confirmationopen={deleteconfirmationopen}
            message={`Want to delete ${deleteId || "this country"} and all of its states?`}
          />

          <Modal
            open={isViewStatesModalOpen}
            onClose={() => setIsViewStatesModalOpen(false)}
            aria-labelledby="view-states-modal-title"
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "90%",
                maxWidth: "600px",
                maxHeight: "80vh",
                bgcolor: "background.paper",
                boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.16)",
                borderRadius: "20px",
                p: 4,
                outline: "none",
                border: "none",
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography
                    id="view-states-modal-title"
                    fontFamily={FontFamily.Poppins}
                    fontWeight={600}
                    fontSize="1.35rem"
                    color={ThemeColor.BLACK}
                  >
                    States in {selectedCountry?.name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: ThemeColor.GREY }}>
                    {selectedCountry?.states?.length || 0} in total
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {manageAllowed && (
                    <Tooltip title="Edit these states">
                      <IconButton
                        onClick={() => {
                          setIsViewStatesModalOpen(false);
                          navigate(
                            `/CAE/AddCountry/${encodeURIComponent(
                              selectedCountry?.name || ""
                            )}`
                          );
                        }}
                      >
                        <ModeEditIcon
                          fontSize="small"
                          sx={{ fill: ThemeColor.LIGHTPURPLE }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton onClick={() => setIsViewStatesModalOpen(false)}>
                    <Crossicon width="20" height="20" />
                  </IconButton>
                </Box>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Search state..."
                value={stateSearchQuery}
                onChange={(e) => setStateSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  style: {
                    borderRadius: "10px",
                    fontFamily: FontFamily.Poppins,
                  },
                }}
              />

              <Box
                sx={{
                  overflowY: "auto",
                  flex: 1,
                  pr: 1,
                  mt: 1,
                  "&::-webkit-scrollbar": { width: "6px" },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#ccc",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": { background: "#aaa" },
                }}
              >
                {selectedCountry?.states?.filter((state) =>
                  state.toLowerCase().includes(stateSearchQuery.toLowerCase())
                ).length === 0 ? (
                  <Typography
                    align="center"
                    color="text.secondary"
                    fontFamily={FontFamily.Poppins}
                    sx={{ py: 4 }}
                  >
                    No states found
                  </Typography>
                ) : (
                  <Grid container spacing={1.5}>
                    {selectedCountry?.states
                      ?.filter((state) =>
                        state
                          .toLowerCase()
                          .includes(stateSearchQuery.toLowerCase())
                      )
                      .map((state, index) => (
                        <Grid item xs={6} sm={4} key={index}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: "10px",
                              border: "1px solid rgba(0, 0, 0, 0.08)",
                              bgcolor: "rgba(0, 0, 0, 0.01)",
                              fontFamily: FontFamily.Poppins,
                              fontSize: "0.875rem",
                              fontWeight: 500,
                              textAlign: "center",
                              color: "rgba(0, 0, 0, 0.75)",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                bgcolor: "rgba(19, 85, 155, 0.04)",
                                borderColor: "rgba(19, 85, 155, 0.15)",
                                color: "rgba(19, 85, 155, 1)",
                              },
                            }}
                          >
                            {state}
                          </Box>
                        </Grid>
                      ))}
                  </Grid>
                )}
              </Box>
            </Box>
          </Modal>
        </>
      )}
    </div>
  );
};

function Countrylist() {
  const dispatch = useDispatch();

  const handleSearch = (searchValue) => {
    dispatch(GetCountryAction({ page: 1, limit: 50, search: searchValue }));
  };

  return (
    <div>
      <ResponsiveDrawer
        maincontent={<CountryListPage />}
        globalsearch={<GlobalSearch onSearch={handleSearch} />}
      />
    </div>
  );
}

export default Countrylist;
